import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { LocationAutocomplete } from '@/components/ui/location-autocomplete';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useGeolocation } from '@/hooks/useGeolocation';
import { OnboardingCompletion } from './OnboardingCompletion';
import { User, MapPin, Plane, Luggage, Check, Edit2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface UserPreferences {
  age_range?: string;
  gender?: string;
  location?: string;
  temperature_unit?: 'celsius' | 'fahrenheit';
  preferred_trip_types?: string[];
  packing_style?: string;
  clothing_styles?: string[];
  onboarding_completed?: boolean;
  onboarding_step?: number;
}

interface OnboardingFlowProps {
  onComplete: () => void;
}

export function OnboardingFlow({ onComplete }: OnboardingFlowProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [preferences, setPreferences] = useState<UserPreferences>({
    preferred_trip_types: [],
    clothing_styles: [],
    onboarding_step: 1
  });
  const [loading, setLoading] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [editingField, setEditingField] = useState<string | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  const { location: geoLocation, loading: geoLoading } = useGeolocation();

  const totalSteps = 4;

  // Pre-populate location with geolocation
  useEffect(() => {
    if (geoLocation.location && !preferences.location) {
      updatePreferences({ location: geoLocation.location });
    }
  }, [geoLocation]);

  useEffect(() => {
    loadExistingPreferences();
  }, [user]);

  const loadExistingPreferences = async () => {
    if (!user) {
      return;
    }

    const { data, error } = await supabase
      .from('user_preferences')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (error) {
      console.error('Error loading preferences');
      toast({
        title: "Error",
        description: "Failed to load your preferences. Please try refreshing the page.",
        variant: "destructive"
      });
      return;
    }

    if (data) {
      const typedData: UserPreferences = {
        ...data,
        temperature_unit: data.temperature_unit === 'fahrenheit' ? 'fahrenheit' : 'celsius'
      };
      setPreferences(typedData);
      setCurrentStep(data.onboarding_step || 1);
    }
  };

  const updatePreferences = (updates: Partial<UserPreferences>) => {
    setPreferences(prev => ({ ...prev, ...updates }));
  };

  const saveProgress = async (stepData: Partial<UserPreferences>) => {
    if (!user) {
      return;
    }

    setLoading(true);
    const updatedPreferences = { 
      ...preferences, 
      ...stepData,
      onboarding_step: currentStep,
      user_id: user.id
    };

    const { error } = await supabase
      .from('user_preferences')
      .upsert(updatedPreferences, { onConflict: 'user_id' });

    if (error) {
      console.error('Error saving preferences');
      toast({
        title: "Error",
        description: "Failed to save preferences. Please try again.",
        variant: "destructive"
      });
    }
    setLoading(false);
  };

  const validateStep = () => {
    const errors: Record<string, string> = {};

    if (currentStep === 1) {
      if (!preferences.age_range) {
        errors.age_range = 'Please select your age range';
      }
      if (!preferences.location) {
        errors.location = 'Please enter your location';
      }
    }

    if (currentStep === 2) {
      if (!preferences.preferred_trip_types || preferences.preferred_trip_types.length === 0) {
        errors.preferred_trip_types = 'Please select at least one trip type';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = async () => {
    if (!validateStep()) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields to continue.",
        variant: "destructive"
      });
      return;
    }

    await saveProgress(preferences);
    
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      setValidationErrors({});
    } else {
      await completeOnboarding();
    }
  };

  const completeOnboarding = async () => {
    if (!user) return;

    setLoading(true);
    const { error } = await supabase
      .from('user_preferences')
      .upsert({
        ...preferences,
        user_id: user.id,
        onboarding_completed: true,
        onboarding_step: totalSteps
      }, { onConflict: 'user_id' });

    if (error) {
      toast({
        title: "Error",
        description: "Failed to complete onboarding. Please try again.",
        variant: "destructive"
      });
      console.error('Error completing onboarding:', error);
      setLoading(false);
    } else {
      setShowCompletion(true);
      setTimeout(() => {
        onComplete();
      }, 3000);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const toggleArrayValue = (key: keyof UserPreferences, value: string) => {
    const current = preferences[key] as string[] || [];
    const updated = current.includes(value)
      ? current.filter(item => item !== value)
      : [...current, value];
    updatePreferences({ [key]: updated });
  };

  const stepIcons = [User, MapPin, Plane, Luggage];
  const stepTitles = ['About You', 'Travel Style', 'Packing', 'Review'];

  const getMissingFields = () => {
    const missing: string[] = [];
    if (!preferences.age_range) missing.push('Age range');
    if (!preferences.location) missing.push('Location');
    if (!preferences.preferred_trip_types?.length) missing.push('Trip types');
    if (!preferences.packing_style) missing.push('Packing style');
    return missing;
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <form className="space-y-6" role="form" aria-label="Personal information">
            <div className="flex items-start gap-3">
              <User className="w-6 h-6 text-primary mt-1 flex-shrink-0" aria-hidden="true" />
              <div className="flex-1">
                <h2 className="text-xl font-semibold mb-2">Tell us about yourself</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Help us personalize your travel recommendations
                </p>
              </div>
            </div>
            
            <fieldset className="space-y-4">
              <legend className="sr-only">Personal Information</legend>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="age_range" className="flex items-center gap-1">
                    Age Range <span className="text-destructive" aria-label="required">*</span>
                  </Label>
                  <Select 
                    value={preferences.age_range || ''} 
                    onValueChange={(value) => {
                      updatePreferences({ age_range: value });
                      setValidationErrors(prev => ({ ...prev, age_range: '' }));
                    }}
                  >
                    <SelectTrigger 
                      id="age_range"
                      aria-required="true"
                      aria-invalid={!!validationErrors.age_range}
                      className={cn(validationErrors.age_range && "border-destructive")}
                    >
                      <SelectValue placeholder="Select age range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="16-25">16-25</SelectItem>
                      <SelectItem value="26-35">26-35</SelectItem>
                      <SelectItem value="36-45">36-45</SelectItem>
                      <SelectItem value="46-55">46-55</SelectItem>
                      <SelectItem value="56-65">56-65</SelectItem>
                      <SelectItem value="65+">65+</SelectItem>
                    </SelectContent>
                  </Select>
                  {validationErrors.age_range && (
                    <p className="text-sm text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {validationErrors.age_range}
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="gender" className="flex items-center gap-1">
                    Gender <span className="text-muted-foreground text-xs">(Optional)</span>
                  </Label>
                  <Select value={preferences.gender || ''} onValueChange={(value) => updatePreferences({ gender: value })}>
                    <SelectTrigger id="gender">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="non-binary">Non-binary</SelectItem>
                      <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2 col-span-2">
                  <Label className="flex items-center gap-1">
                    Current Location <span className="text-destructive" aria-label="required">*</span>
                    {geoLoading && <span className="text-xs text-muted-foreground">(detecting...)</span>}
                  </Label>
                  <LocationAutocomplete
                    value={preferences.location || ''}
                    onChange={(value) => {
                      updatePreferences({ location: value });
                      setValidationErrors(prev => ({ ...prev, location: '' }));
                    }}
                    placeholder="Search for your current city or country..."
                    aria-required="true"
                    aria-invalid={!!validationErrors.location}
                  />
                  {validationErrors.location && (
                    <p className="text-sm text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {validationErrors.location}
                    </p>
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="temperature_unit" className="flex items-center gap-1">
                  Temperature Unit <span className="text-muted-foreground text-xs">(Optional)</span>
                </Label>
                <Select 
                  value={preferences.temperature_unit || 'celsius'} 
                  onValueChange={(value) => updatePreferences({ temperature_unit: value as 'celsius' | 'fahrenheit' })}
                >
                  <SelectTrigger id="temperature_unit">
                    <SelectValue placeholder="Select temperature unit" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="celsius">Celsius (°C)</SelectItem>
                    <SelectItem value="fahrenheit">Fahrenheit (°F)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </fieldset>
          </form>
        );

      case 2:
        const tripTypeIcons: Record<string, string> = {
          'Adventure': '🏔️',
          'Cultural': '🏛️',
          'Relaxation': '🏖️',
          'Business': '💼',
          'Family': '👨‍👩‍👧‍👦',
          'Solo': '🎒',
          'Group': '👥',
          'Romantic': '💑'
        };
        
        return (
          <form className="space-y-6" role="form" aria-label="Travel preferences">
            <div className="flex items-start gap-3">
              <Plane className="w-6 h-6 text-primary mt-1 flex-shrink-0" aria-hidden="true" />
              <div className="flex-1">
                <h2 className="text-xl font-semibold mb-2">What's your travel style?</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Select all types of trips that interest you
                </p>
              </div>
            </div>
            
            <fieldset className="space-y-4">
              <legend className="sr-only">Trip Type Preferences</legend>
              
              <Label className="flex items-center gap-1">
                Preferred Trip Types <span className="text-destructive" aria-label="required">*</span>
              </Label>
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {['Adventure', 'Cultural', 'Relaxation', 'Business', 'Family', 'Solo', 'Group', 'Romantic'].map((type) => {
                  const isSelected = (preferences.preferred_trip_types || []).includes(type);
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => {
                        toggleArrayValue('preferred_trip_types', type);
                        setValidationErrors(prev => ({ ...prev, preferred_trip_types: '' }));
                      }}
                      className={cn(
                        "relative p-4 rounded-lg border-2 transition-all duration-200",
                        "flex flex-col items-center gap-2 text-center",
                        "hover:shadow-md active:scale-95",
                        "min-h-[100px] touch-manipulation",
                        isSelected 
                          ? "border-primary bg-primary/10 shadow-sm" 
                          : "border-border hover:border-primary/50"
                      )}
                      role="checkbox"
                      aria-checked={isSelected}
                      aria-label={`${type} trips`}
                    >
                      <span className="text-3xl" aria-hidden="true">{tripTypeIcons[type]}</span>
                      <span className="text-sm font-medium">{type}</span>
                      {isSelected && (
                        <Check className="absolute top-2 right-2 w-4 h-4 text-primary" aria-hidden="true" />
                      )}
                    </button>
                  );
                })}
              </div>
              
              {validationErrors.preferred_trip_types && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {validationErrors.preferred_trip_types}
                </p>
              )}
            </fieldset>
          </form>
        );

      case 3:
        const styleIcons: Record<string, string> = {
          'Casual': '👕',
          'Business': '👔',
          'Formal': '🤵',
          'Athletic': '👟',
          'Bohemian': '🌺',
          'Classic': '👗',
          'Trendy': '✨',
          'Comfortable': '🧸'
        };
        
        return (
          <form className="space-y-6" role="form" aria-label="Packing preferences">
            <div className="flex items-start gap-3">
              <Luggage className="w-6 h-6 text-primary mt-1 flex-shrink-0" aria-hidden="true" />
              <div className="flex-1">
                <h2 className="text-xl font-semibold mb-2">Packing preferences</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Tell us about your packing and style preferences
                </p>
              </div>
            </div>
            
            <fieldset className="space-y-6">
              <legend className="sr-only">Packing and Style Preferences</legend>
              
              <div className="space-y-3">
                <Label htmlFor="packing_style" className="flex items-center gap-1">
                  Packing Style <span className="text-muted-foreground text-xs">(Optional)</span>
                </Label>
                <Select 
                  value={preferences.packing_style || ''} 
                  onValueChange={(value) => updatePreferences({ packing_style: value })}
                >
                  <SelectTrigger id="packing_style">
                    <SelectValue placeholder="How do you usually pack?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minimalist">🎒 Minimalist - Pack only essentials</SelectItem>
                    <SelectItem value="balanced">⚖️ Balanced - Practical approach</SelectItem>
                    <SelectItem value="overpacker">🧳 Overpacker - Extra options</SelectItem>
                    <SelectItem value="just-in-case">📦 Just-in-case - Prepared for everything</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-3">
                <Label className="flex items-center gap-1">
                  Clothing Styles <span className="text-muted-foreground text-xs">(Optional)</span>
                </Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {['Casual', 'Business', 'Formal', 'Athletic', 'Bohemian', 'Classic', 'Trendy', 'Comfortable'].map((style) => {
                    const isSelected = (preferences.clothing_styles || []).includes(style);
                    return (
                      <button
                        key={style}
                        type="button"
                        onClick={() => toggleArrayValue('clothing_styles', style)}
                        className={cn(
                          "relative p-3 rounded-lg border-2 transition-all duration-200",
                          "flex flex-col items-center gap-1 text-center",
                          "hover:shadow-md active:scale-95",
                          "min-h-[80px] touch-manipulation",
                          isSelected 
                            ? "border-primary bg-primary/10 shadow-sm" 
                            : "border-border hover:border-primary/50"
                        )}
                        role="checkbox"
                        aria-checked={isSelected}
                        aria-label={`${style} clothing style`}
                      >
                        <span className="text-2xl" aria-hidden="true">{styleIcons[style]}</span>
                        <span className="text-xs font-medium">{style}</span>
                        {isSelected && (
                          <Check className="absolute top-1 right-1 w-3 h-3 text-primary" aria-hidden="true" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </fieldset>
          </form>
        );

      case 4:
        const missingFields = getMissingFields();
        
        return (
          <div className="space-y-6">
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-primary mt-1 flex-shrink-0" aria-hidden="true" />
              <div className="flex-1">
                <h2 className="text-xl font-semibold mb-2">Review your profile</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Check your information and make any final edits
                </p>
              </div>
            </div>

            <div className="space-y-4">
              {/* Personal Info */}
              <div className="rounded-lg border bg-card p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <User className="w-5 h-5 text-primary" aria-hidden="true" />
                    <h3 className="font-semibold">Personal Information</h3>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentStep(1)}
                    aria-label="Edit personal information"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </div>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Age Range:</dt>
                    <dd className="font-medium">{preferences.age_range || <span className="text-destructive">Not set</span>}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Gender:</dt>
                    <dd className="font-medium">{preferences.gender || 'Not specified'}</dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Location:</dt>
                    <dd className="font-medium">{preferences.location || <span className="text-destructive">Not set</span>}</dd>
                  </div>
                </dl>
              </div>

              {/* Travel Preferences */}
              <div className="rounded-lg border bg-card p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Plane className="w-5 h-5 text-primary" aria-hidden="true" />
                    <h3 className="font-semibold">Travel Preferences</h3>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentStep(2)}
                    aria-label="Edit travel preferences"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </div>
                <dl className="space-y-2 text-sm">
                  <div>
                    <dt className="text-muted-foreground mb-1">Trip Types:</dt>
                    <dd className="font-medium">
                      {preferences.preferred_trip_types?.length ? (
                        <div className="flex flex-wrap gap-1">
                          {preferences.preferred_trip_types.map(type => (
                            <span key={type} className="px-2 py-1 rounded bg-primary/10 text-primary text-xs">
                              {type}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="text-destructive">Not set</span>
                      )}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Packing & Style */}
              <div className="rounded-lg border bg-card p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Luggage className="w-5 h-5 text-primary" aria-hidden="true" />
                    <h3 className="font-semibold">Packing & Style</h3>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCurrentStep(3)}
                    aria-label="Edit packing preferences"
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </div>
                <dl className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-muted-foreground">Packing Style:</dt>
                    <dd className="font-medium">{preferences.packing_style || 'Not specified'}</dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground mb-1">Clothing Styles:</dt>
                    <dd className="font-medium">
                      {preferences.clothing_styles?.length ? (
                        <div className="flex flex-wrap gap-1">
                          {preferences.clothing_styles.map(style => (
                            <span key={style} className="px-2 py-1 rounded bg-secondary text-secondary-foreground text-xs">
                              {style}
                            </span>
                          ))}
                        </div>
                      ) : (
                        'Not specified'
                      )}
                    </dd>
                  </div>
                </dl>
              </div>

              {/* Missing Fields Warning */}
              {missingFields.length > 0 && (
                <div className="rounded-lg border border-[hsl(var(--brand-yellow))] bg-[hsl(var(--brand-yellow))]/10 p-4">
                  <div className="flex gap-3">
                    <AlertCircle className="w-5 h-5 text-[hsl(var(--brand-yellow))] flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-sm mb-1">Optional fields not filled</h4>
                      <p className="text-xs text-muted-foreground mb-2">
                        Adding this information will help us personalize your experience:
                      </p>
                      <ul className="text-xs space-y-1">
                        {missingFields.map(field => (
                          <li key={field}>• {field}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (showCompletion) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary/10 to-secondary/10">
        <Card className="w-full max-w-2xl">
          <CardContent className="pt-6">
            <OnboardingCompletion />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-primary/10 to-secondary/10">
      <Card className="w-full max-w-2xl" role="main" aria-label="Onboarding wizard">
        <CardHeader>
          <CardTitle className="text-2xl text-center">
            <h1 className="text-2xl font-semibold">Get to Know You</h1>
          </CardTitle>
          <CardDescription className="text-center">
            Help us personalize your travel experience with AI-powered recommendations
          </CardDescription>
          
          {/* Step indicators */}
          <div className="flex justify-between items-center mt-6 mb-2" role="navigation" aria-label="Onboarding steps">
            {Array.from({ length: totalSteps }).map((_, index) => {
              const stepNum = index + 1;
              const StepIcon = stepIcons[index];
              const isCompleted = stepNum < currentStep;
              const isCurrent = stepNum === currentStep;
              
              return (
                <div key={stepNum} className="flex flex-col items-center flex-1">
                  <div
                    className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200",
                      "border-2",
                      isCurrent && "border-primary bg-primary text-primary-foreground shadow-md scale-110",
                      isCompleted && "border-primary bg-primary/20 text-primary",
                      !isCurrent && !isCompleted && "border-border bg-background text-muted-foreground"
                    )}
                    aria-current={isCurrent ? "step" : undefined}
                    aria-label={`Step ${stepNum}: ${stepTitles[index]}`}
                  >
                    {isCompleted ? (
                      <Check className="w-5 h-5" aria-hidden="true" />
                    ) : (
                      <StepIcon className="w-5 h-5" aria-hidden="true" />
                    )}
                  </div>
                  <span className={cn(
                    "text-xs mt-2 font-medium transition-colors hidden sm:block",
                    isCurrent && "text-foreground",
                    !isCurrent && "text-muted-foreground"
                  )}>
                    {stepTitles[index]}
                  </span>
                </div>
              );
            })}
          </div>
          
          <Progress value={(currentStep / totalSteps) * 100} className="mt-4" aria-label="Overall progress" />
        </CardHeader>
        
        <CardContent className="min-h-[450px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>
        </CardContent>
        
        <CardFooter className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 1 || loading}
            aria-label="Go to previous step"
          >
            Previous
          </Button>
          
          <div className="text-sm text-muted-foreground" aria-live="polite">
            Step {currentStep} of {totalSteps}
          </div>
          
          <Button
            onClick={handleNext}
            disabled={loading}
            aria-label={currentStep === totalSteps ? 'Complete onboarding' : 'Go to next step'}
          >
            {loading ? 'Saving...' : currentStep === totalSteps ? 'Complete' : 'Next'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}